/** Affiliasjon → CoT-identitetsbokstav (a-<x>-…). */
const AFFIL_TO_COT = { friend: 'f', hostile: 'h', neutral: 'n', unknown: 'u' };
const COT_TO_AFFIL = { f: 'friend', a: 'friend', h: 'hostile', n: 'neutral', u: 'unknown', p: 'unknown', s: 'unknown', j: 'hostile', k: 'hostile' };
/** SIDC battle-dimension (pos 3) → CoT dimensjonsbokstav. */
function sidcDim(sidc) {
    const d = (sidc[2] ?? 'G').toUpperCase();
    return ['G', 'A', 'S', 'U', 'P'].includes(d) ? d : 'G';
}
/** CoT-atomtype for et spor, f.eks. «a-h-A» (fiendtlig luft). */
export function trackToCotType(t) {
    return `a-${AFFIL_TO_COT[t.affiliation]}-${sidcDim(t.sidc)}`;
}
/** CoT-identitetsbokstav → SKADE-affiliasjon. */
export function cotTypeToAffiliation(type) {
    const m = /^a-([a-z])-/i.exec(type);
    return (m && COT_TO_AFFIL[m[1].toLowerCase()]) || 'unknown';
}
function iso(ms) {
    const d = new Date(ms);
    // Utenfor Date-området (±8.64e15) → Invalid Date kaster i toISOString; fall
    // tilbake til epoch så en enkelt korrupt tsMs ikke velter hele CoT-eksporten.
    return Number.isNaN(d.getTime()) ? new Date(0).toISOString() : d.toISOString();
}
/** XML-escape for attributtverdier (label/remarks kan være angriperstyrt). */
export function xmlEsc(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}
/** Ett spor → ett CoT-event (XML-streng). */
export function trackToCotXml(t, opts = {}) {
    const now = opts.now ?? t.tsMs;
    const staleSec = opts.staleSec ?? 300;
    const time = iso(t.tsMs || now);
    const stale = iso((t.tsMs || now) + staleSec * 1000);
    const ce = t.cepM !== undefined ? t.cepM.toFixed(1) : '9999999.0';
    const remarks = Object.entries(t.extra ?? {}).map(([k, v]) => `${k}: ${v}`).join('; ');
    return (`<event version="2.0" uid="${xmlEsc(t.id)}" type="${xmlEsc(trackToCotType(t))}"` +
        ` time="${time}" start="${time}" stale="${stale}" how="m-g">` +
        `<point lat="${t.lat}" lon="${t.lon}" hae="9999999.0" ce="${ce}" le="9999999.0"/>` +
        `<detail>` +
        `<contact callsign="${xmlEsc(t.label)}"/>` +
        `<__group name="${xmlEsc(t.affiliation)}"/>` +
        (remarks ? `<remarks>${xmlEsc(remarks)}</remarks>` : '') +
        `</detail></event>`);
}
/** Flere spor → én CoT-batch (container). TAK-strømmer tar ett event om gangen;
 * denne wrapperen er for poll/eksport-endepunkt. */
export function tracksToCotXml(tracks, opts = {}) {
    const parts = [];
    for (const t of tracks) {
        try {
            parts.push(trackToCotXml(t, opts));
        }
        catch { /* dropp ett korrupt spor, ikke hele batchen */ }
    }
    return `<events>${parts.join('')}</events>`;
}
function attr(xml, name) {
    const m = new RegExp(`\\b${name}="([^"]*)"`).exec(xml);
    return m ? m[1] : undefined;
}
/** Parser ett CoT-event (flat struktur) → felt vi trenger. null hvis ugyldig. */
export function parseCotEvent(xml) {
    const ev = /<event\b[^>]*>/i.exec(xml);
    const pt = /<point\b[^>]*\/?>/i.exec(xml);
    if (!ev || !pt)
        return null;
    const uid = attr(ev[0], 'uid');
    const type = attr(ev[0], 'type') ?? '';
    const lat = Number(attr(pt[0], 'lat'));
    const lon = Number(attr(pt[0], 'lon'));
    if (!uid || !Number.isFinite(lat) || !Number.isFinite(lon))
        return null;
    // Avvis koordinater utenfor gyldig område (hindrer søppel-CoT i bildet).
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180)
        return null;
    const timeStr = attr(ev[0], 'time') ?? attr(ev[0], 'start');
    const tsMs = timeStr ? Date.parse(timeStr) : NaN;
    const csM = /<contact\b[^>]*\bcallsign="([^"]*)"/i.exec(xml);
    const ceStr = attr(pt[0], 'ce');
    const ce = ceStr !== undefined ? Number(ceStr) : undefined;
    return {
        uid,
        type,
        affiliation: cotTypeToAffiliation(type),
        lat,
        lon,
        tsMs: Number.isFinite(tsMs) ? tsMs : 0,
        callsign: csM ? xmlUnesc(csM[1]) : undefined,
        ce: ce !== undefined && Number.isFinite(ce) && ce < 9_000_000 ? ce : undefined,
    };
}
function xmlUnesc(s) {
    return s
        .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
        .replace(/&amp;/g, '&');
}
/** CoT-affiliasjon+dim → et enkelt ramme-SIDC for rendering. */
function affilDimToSidc(aff, type) {
    const a = { friend: 'F', hostile: 'H', neutral: 'N', unknown: 'U' }[aff];
    const dimM = /^a-[a-z]-([A-Za-z])/.exec(type);
    const dim = dimM ? dimM[1].toUpperCase() : 'G';
    return `S${a}${['G', 'A', 'S', 'U', 'P'].includes(dim) ? dim : 'G'}P------`.padEnd(15, '-');
}
/** Kilde-merke for spor som kom fra en GNÅ-brukers egen telefon (BFT), til
 * forskjell fra `'cot'` som er ATAK/WinTAK/TAK-server. Uten dette skillet er
 * det umulig i COP å se hvilke spor som er våre egne folk i GNÅ. */
export const GNA_BFT_SOURCE = 'gna-bft';
/** Parset CoT → SKADE-Track (innkommende fra ATAK/TAK).
 *
 * `source` kan overstyres av kalleren: GNÅ-broen sender BFT gjennom samme
 * CoT-vei (én inntaksvei å vedlikeholde), men sporene skal likevel kunne
 * skilles fra TAK-spor i COP. */
export function cotToTrack(p, opts = {}) {
    const kind = p.affiliation === 'friend' ? 'unit' : 'point';
    return {
        id: `cot:${p.uid}`,
        kind,
        affiliation: p.affiliation,
        lat: p.lat,
        lon: p.lon,
        sidc: affilDimToSidc(p.affiliation, p.type),
        label: p.callsign || p.uid,
        tsMs: p.tsMs || 0,
        cepM: p.ce,
        source: opts.source ?? 'cot',
    };
}
const tall = (v) => {
    const n = typeof v === 'string' ? Number(v) : v;
    return typeof n === 'number' && Number.isFinite(n) ? n : undefined;
};
/**
 * GNÅ BFT-event → CoT-XML, eller null når posisjonen ikke er brukbar.
 *
 * Merk at `stale` respekteres: en gammel posisjon skal ikke stå som fersk i
 * situasjonsbildet. Uten `stale` settes 5 minutter, som er GNÅ sitt eget
 * hjerteslag-intervall × 5.
 */
export function bftTilCotXml(content, senderId) {
    const lat = tall(content.lat);
    const lon = tall(content.lon);
    if (lat === undefined || lon === undefined)
        return null;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180)
        return null;
    const uid = typeof content.uid === 'string' && content.uid
        ? content.uid
        : (senderId || '').replace(/^@/, '').replace(/:.*$/, '');
    if (!uid)
        return null;
    const ts = tall(content.ts) ?? 0;
    const stale = tall(content.stale) ?? (ts ? ts + 5 * 60_000 : 0);
    const iso = (ms) => new Date(ms || Date.now()).toISOString();
    const type = typeof content.cot_type === 'string' && content.cot_type
        ? content.cot_type
        : 'a-f-G-U-C'; // egen bakkeenhet — GNÅ-brukere er per definisjon egne
    const callsign = typeof content.callsign === 'string' ? content.callsign : uid;
    const hae = tall(content.hae) ?? 0;
    const course = tall(content.course);
    const speed = tall(content.speed);
    return [
        `<event version="2.0" uid="${xmlEsc(uid)}" type="${xmlEsc(type)}" how="m-g"`,
        ` time="${iso(ts)}" start="${iso(ts)}" stale="${iso(stale)}">`,
        `<point lat="${lat}" lon="${lon}" hae="${hae}" ce="9999999" le="9999999"/>`,
        '<detail>',
        `<contact callsign="${xmlEsc(callsign)}"/>`,
        course !== undefined && speed !== undefined
            ? `<track course="${course}" speed="${speed}"/>`
            : '',
        '</detail>',
        '</event>',
    ].join('');
}
//# sourceMappingURL=cot.js.map